/**
 * Discounts Zustand Store
 *
 * Manages state for the Discounts & Promo Codes section including:
 * - Discount list with filters and pagination
 * - Active discount for editing
 * - CRUD operations
 */
import { create } from 'zustand';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import type {
  DiscountWithStats,
  DiscountListFilters,
  PaginationMeta,
  CreateDiscountDto,
  UpdateDiscountDto,
} from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface DiscountsError {
  code: string;
  message: string;
}

// ============================================
// State Interface
// ============================================

export interface DiscountsState {
  // List
  discounts: DiscountWithStats[];
  pagination: PaginationMeta | null;
  isLoadingList: boolean;
  listError: DiscountsError | null;

  // Filters
  filters: DiscountListFilters;

  // Active Discount (for editing)
  activeDiscount: DiscountWithStats | null;
  isLoadingDiscount: boolean;
  discountError: DiscountsError | null;

  // Action States
  isPerformingAction: boolean;
  actionError: DiscountsError | null;

  // Actions - List
  loadDiscounts: (page?: number) => Promise<void>;
  refreshDiscounts: () => Promise<void>;

  // Actions - Filters
  setFilter: <K extends keyof DiscountListFilters>(key: K, value: DiscountListFilters[K]) => void;
  clearFilters: () => void;

  // Actions - Active
  loadDiscount: (id: string) => Promise<void>;
  selectDiscount: (discount: DiscountWithStats) => void;
  clearActiveDiscount: () => void;

  // Actions - CRUD
  createDiscount: (dto: CreateDiscountDto) => Promise<string | null>;
  updateDiscount: (id: string, dto: UpdateDiscountDto) => Promise<boolean>;
  deleteDiscount: (id: string) => Promise<boolean>;

  // Utility
  clearError: () => void;
  reset: () => void;
}

// ============================================
// Initial State
// ============================================

const getInitialState = () => ({
  discounts: [] as DiscountWithStats[],
  pagination: null as PaginationMeta | null,
  isLoadingList: false,
  listError: null as DiscountsError | null,

  filters: {} as DiscountListFilters,

  activeDiscount: null as DiscountWithStats | null,
  isLoadingDiscount: false,
  discountError: null as DiscountsError | null,

  isPerformingAction: false,
  actionError: null as DiscountsError | null,
});

// ============================================
// Store Implementation
// ============================================

export const useDiscountsStore = create<DiscountsState>((set, get) => ({
  ...getInitialState(),

  // ============================================
  // List Actions
  // ============================================

  loadDiscounts: async (page = 1) => {
    set({ isLoadingList: true, listError: null });
    try {
      const api = getApiClient();
      const { filters } = get();
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', '20');

      if (filters.search) params.set('search', filters.search);
      if (filters.type) params.set('type', filters.type);
      if (filters.scope) params.set('scope', filters.scope);
      if (filters.isActive !== undefined) params.set('isActive', String(filters.isActive));
      if (filters.hasCode !== undefined) params.set('hasCode', String(filters.hasCode));

      const response = await api.get<{ data: DiscountWithStats[]; pagination: PaginationMeta }>(
        `${API_ENDPOINTS.SUPER_ADMIN.DISCOUNTS.LIST}?${params.toString()}`
      );

      set({
        discounts: response.data,
        pagination: response.pagination,
        isLoadingList: false,
      });
    } catch (error) {
      set({
        isLoadingList: false,
        listError: {
          code: 'LOAD_DISCOUNTS_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load discounts',
        },
      });
    }
  },

  refreshDiscounts: async () => {
    const { pagination } = get();
    await get().loadDiscounts(pagination?.page || 1);
  },

  // ============================================
  // Filter Actions
  // ============================================

  setFilter: (key, value) => {
    set((state) => ({
      filters: { ...state.filters, [key]: value },
    }));
    get().loadDiscounts(1);
  },

  clearFilters: () => {
    set({ filters: {} });
    get().loadDiscounts(1);
  },

  // ============================================
  // Active Discount Actions
  // ============================================

  loadDiscount: async (id: string) => {
    set({ isLoadingDiscount: true, discountError: null });
    try {
      const api = getApiClient();
      const response = await api.get<DiscountWithStats>(
        API_ENDPOINTS.SUPER_ADMIN.DISCOUNTS.GET(id)
      );
      set({ activeDiscount: response, isLoadingDiscount: false });
    } catch (error) {
      set({
        isLoadingDiscount: false,
        discountError: {
          code: 'LOAD_DISCOUNT_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load discount',
        },
      });
    }
  },

  selectDiscount: (discount: DiscountWithStats) => {
    set({ activeDiscount: discount });
  },

  clearActiveDiscount: () => {
    set({ activeDiscount: null, discountError: null });
  },

  // ============================================
  // CRUD Actions
  // ============================================

  createDiscount: async (dto: CreateDiscountDto): Promise<string | null> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      const response = await api.post<DiscountWithStats>(
        API_ENDPOINTS.SUPER_ADMIN.DISCOUNTS.CREATE,
        dto
      );
      await get().refreshDiscounts();
      set({ isPerformingAction: false });
      return response.id;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'CREATE_DISCOUNT_FAILED',
          message: error instanceof Error ? error.message : 'Failed to create discount',
        },
      });
      return null;
    }
  },

  updateDiscount: async (id: string, dto: UpdateDiscountDto): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      const response = await api.put<DiscountWithStats>(
        API_ENDPOINTS.SUPER_ADMIN.DISCOUNTS.UPDATE(id),
        dto
      );

      set((state) => ({
        discounts: state.discounts.map((d) =>
          d.id === id ? { ...d, ...response } : d
        ),
        activeDiscount:
          state.activeDiscount?.id === id
            ? { ...state.activeDiscount, ...response }
            : state.activeDiscount,
        isPerformingAction: false,
      }));

      return true;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'UPDATE_DISCOUNT_FAILED',
          message: error instanceof Error ? error.message : 'Failed to update discount',
        },
      });
      return false;
    }
  },

  deleteDiscount: async (id: string): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      await api.delete(API_ENDPOINTS.SUPER_ADMIN.DISCOUNTS.DELETE(id));

      set((state) => ({
        discounts: state.discounts.filter((d) => d.id !== id),
        activeDiscount: state.activeDiscount?.id === id ? null : state.activeDiscount,
        isPerformingAction: false,
      }));

      return true;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'DELETE_DISCOUNT_FAILED',
          message: error instanceof Error ? error.message : 'Failed to delete discount',
        },
      });
      return false;
    }
  },

  // ============================================
  // Utility
  // ============================================

  clearError: () => {
    set({ listError: null, discountError: null, actionError: null });
  },

  reset: () => {
    set(getInitialState());
  },
}));
